/**
 * RefundConfirmationModal Component - Modal for sending refund confirmation
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Chip,
  IconButton,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { Close, Send, Email, WhatsApp, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  previewRefundConfirmationEmail,
  sendRefundConfirmation,
} from "../services/refundService.js";
import { logError } from "../../../shared/utils/logger.js";

/**
 * RefundConfirmationModal Component
 */
export const RefundConfirmationModal = ({
  open,
  onClose,
  invoice,
  refund,
  refundIndex,
  onSent,
}) => {
  const [emailContent, setEmailContent] = useState("");
  const [whatsappContent, setWhatsappContent] = useState("");
  const [ccEmails, setCcEmails] = useState([]);
  const [bccEmails, setBccEmails] = useState([]);
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendWhatsApp, setSendWhatsApp] = useState(true);
  const [showWhatsAppPreview, setShowWhatsAppPreview] = useState(false);
  const [customerEmail, setCustomerEmail] = useState(null);
  const [customerPhone, setCustomerPhone] = useState(null);
  const [emailFound, setEmailFound] = useState(false);
  const [phoneFound, setPhoneFound] = useState(false);

  // Load email preview when modal opens
  useEffect(() => {
    if (open && invoice && refund) {
      loadEmailPreview();
    }
  }, [open, invoice, refund]);

  const loadEmailPreview = async () => {
    try {
      setPreviewLoading(true);
      setError(null);
      const result = await previewRefundConfirmationEmail(
        invoice.id,
        refundIndex
      );
      if (result.success) {
        // API response structure:
        // Backend: { success: true, data: { emailContent, whatsappContent, ... } }
        // api.get() returns: { success: true, data: { emailContent, whatsappContent, ... } }
        // Service wraps: { success: true, data: response.data }
        // So result.data = { emailContent, whatsappContent, customerEmail, customerPhone }
        const previewData = result.data;
        
        if (previewData) {
          setEmailContent(previewData.emailContent || "");
          setWhatsappContent(previewData.whatsappContent || "");
          setCustomerEmail(previewData.customerEmail || null);
          setCustomerPhone(previewData.customerPhone || null);
          setEmailFound(previewData.emailFound || false);
          setPhoneFound(previewData.phoneFound || false);
        } else {
          setError("No preview data received from server");
        }
      } else {
        setError(result.error || "Failed to load email preview");
      }
    } catch (err) {
      setError("Failed to load email preview");
      logError("Load email preview error", { error: err.message });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleAddCc = () => {
    if (ccInput.trim() && isValidEmail(ccInput.trim())) {
      setCcEmails([...ccEmails, ccInput.trim()]);
      setCcInput("");
    }
  };

  const handleAddBcc = () => {
    if (bccInput.trim() && isValidEmail(bccInput.trim())) {
      setBccEmails([...bccEmails, bccInput.trim()]);
      setBccInput("");
    }
  };

  const handleRemoveCc = (index) => {
    setCcEmails(ccEmails.filter((_, i) => i !== index));
  };

  const handleRemoveBcc = (index) => {
    setBccEmails(bccEmails.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    setError(null);

    if (!emailContent.trim()) {
      setError("Email content is required");
      return;
    }

    if (!sendEmail && !sendWhatsApp) {
      setError("Please enable at least one communication method");
      return;
    }

    try {
      setLoading(true);
      const result = await sendRefundConfirmation(
        invoice.id,
        refundIndex,
        {
          emailContent: emailContent.trim(),
          cc: ccEmails,
          bcc: bccEmails,
          sendEmail,
          sendWhatsApp,
        }
      );

      if (result.success) {
        onSent?.();
        onClose();
      } else {
        setError(result.error || "Failed to send confirmation");
      }
    } catch (err) {
      setError(err.message || "Failed to send confirmation");
      logError("Send refund confirmation error", { error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setEmailContent("");
      setWhatsappContent("");
      setCcEmails([]);
      setBccEmails([]);
      setCcInput("");
      setBccInput("");
      setSendEmail(true);
      setSendWhatsApp(true);
      setShowWhatsAppPreview(false);
      setCustomerEmail(null);
      setCustomerPhone(null);
      setEmailFound(false);
      setPhoneFound(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Send Refund Confirmation</Typography>
          <IconButton onClick={handleClose} size="small" disabled={loading}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          Review and edit the email content before sending. A credit note PDF
          will be attached automatically.
        </Alert>

        {/* Contact Info Status */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Contact Information
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: emailFound ? "#e8f5e9" : "#ffebee",
                border: `1px solid ${emailFound ? "#4caf50" : "#f44336"}`,
                flex: 1,
                minWidth: "200px",
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Email color={emailFound ? "success" : "error"} />
                <Typography variant="body2" fontWeight="bold">
                  Email Address
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ mt: 0.5 }}
                color={emailFound ? "text.primary" : "error.main"}
              >
                {emailFound ? (
                  customerEmail
                ) : (
                  "No email address found for this customer"
                )}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: phoneFound ? "#e8f5e9" : "#ffebee",
                border: `1px solid ${phoneFound ? "#4caf50" : "#f44336"}`,
                flex: 1,
                minWidth: "200px",
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <WhatsApp color={phoneFound ? "success" : "error"} />
                <Typography variant="body2" fontWeight="bold">
                  Phone Number
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ mt: 0.5 }}
                color={phoneFound ? "text.primary" : "error.main"}
              >
                {phoneFound ? (
                  customerPhone
                ) : (
                  "No phone number found for this customer"
                )}
              </Typography>
            </Box>
          </Box>
          {!emailFound && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Email cannot be sent because no email address was found. Please
              add an email address to the customer profile or use CC/BCC to
              send to additional recipients.
            </Alert>
          )}
        </Box>

        {previewLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Email Content Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Email Content
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={16}
                label="Email Content"
                value={emailContent}
                onChange={(e) => setEmailContent(e.target.value)}
                sx={{ mb: 2 }}
                required
                helperText="This email will be sent with a credit note PDF attachment"
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* WhatsApp Preview Section */}
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showWhatsAppPreview}
                    onChange={(e) => setShowWhatsAppPreview(e.target.checked)}
                    icon={<VisibilityOff />}
                    checkedIcon={<Visibility />}
                  />
                }
                label={
                  <Typography variant="subtitle1" fontWeight="bold">
                    Show WhatsApp Message Preview
                  </Typography>
                }
              />
            </Box>

            {showWhatsAppPreview && whatsappContent && (
              <Card variant="outlined" sx={{ mb: 2, bgcolor: "#f5f5f5" }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <WhatsApp color="success" />
                    <Typography variant="subtitle2" fontWeight="bold">
                      WhatsApp Message Preview
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "white",
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                      fontFamily: "monospace",
                      whiteSpace: "pre-wrap",
                      fontSize: "0.875rem",
                      lineHeight: 1.6,
                    }}
                  >
                    {whatsappContent}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    This message will be sent via WhatsApp if enabled
                  </Typography>
                </CardContent>
              </Card>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                CC Recipients
              </Typography>
              <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                {ccEmails.map((email, index) => (
                  <Chip
                    key={index}
                    label={email}
                    onDelete={() => handleRemoveCc(index)}
                    size="small"
                  />
                ))}
              </Box>
              <Box display="flex" gap={1}>
                <TextField
                  size="small"
                  placeholder="Enter email address"
                  value={ccInput}
                  onChange={(e) => setCcInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCc();
                    }
                  }}
                  sx={{ flex: 1 }}
                  error={ccInput.trim() && !isValidEmail(ccInput.trim())}
                  helperText={
                    ccInput.trim() && !isValidEmail(ccInput.trim())
                      ? "Invalid email address"
                      : ""
                  }
                />
                <Button
                  size="small"
                  onClick={handleAddCc}
                  variant="outlined"
                  disabled={!ccInput.trim() || !isValidEmail(ccInput.trim())}
                >
                  Add CC
                </Button>
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                BCC Recipients
              </Typography>
              <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                {bccEmails.map((email, index) => (
                  <Chip
                    key={index}
                    label={email}
                    onDelete={() => handleRemoveBcc(index)}
                    size="small"
                  />
                ))}
              </Box>
              <Box display="flex" gap={1}>
                <TextField
                  size="small"
                  placeholder="Enter email address"
                  value={bccInput}
                  onChange={(e) => setBccInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddBcc();
                    }
                  }}
                  sx={{ flex: 1 }}
                  error={bccInput.trim() && !isValidEmail(bccInput.trim())}
                  helperText={
                    bccInput.trim() && !isValidEmail(bccInput.trim())
                      ? "Invalid email address"
                      : ""
                  }
                />
                <Button
                  size="small"
                  onClick={handleAddBcc}
                  variant="outlined"
                  disabled={!bccInput.trim() || !isValidEmail(bccInput.trim())}
                >
                  Add BCC
                </Button>
              </Box>
            </Box>

            <Box display="flex" gap={2}>
              <Button
                variant={sendEmail ? "contained" : "outlined"}
                startIcon={<Email />}
                onClick={() => setSendEmail(!sendEmail)}
                color={sendEmail ? "primary" : "inherit"}
              >
                {sendEmail ? "Email Enabled" : "Email Disabled"}
              </Button>
              <Button
                variant={sendWhatsApp ? "contained" : "outlined"}
                startIcon={<WhatsApp />}
                onClick={() => setSendWhatsApp(!sendWhatsApp)}
                color={sendWhatsApp ? "success" : "inherit"}
              >
                {sendWhatsApp ? "WhatsApp Enabled" : "WhatsApp Disabled"}
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          disabled={
            loading ||
            !emailContent.trim() ||
            (!sendEmail && !sendWhatsApp)
          }
          startIcon={loading ? <CircularProgress size={20} /> : <Send />}
        >
          {loading ? "Sending..." : "Send Confirmation"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefundConfirmationModal;

